import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ArrowUpRight } from "lucide-react";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BUSINESS } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { value: "assembly", label: "New assembly" },
  { value: "disassembly", label: "Disassembly" },
  { value: "relocation", label: "Relocation" },
  { value: "maintenance", label: "Maintenance / tune-up" },
  { value: "quote", label: "General quote / question" },
];

export default function BookingForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    sauna_type: "indoor",
    sauna_model: "",
    service_type: "assembly",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.address || !form.sauna_model) {
      toast.error("Please fill in name, email, phone, address, and sauna model.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setSubmitted(true);
      toast.success("Request received — we'll be in touch within 24 hours.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please review the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="border-t border-[#E5E0D8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="overline mb-5">Book · Quote · Consultation</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl text-text-main leading-tight">
              Tell us about
              <br />
              <span className="italic text-cedar">your sauna.</span>
            </h2>
            <p className="text-text-muted mt-6 text-base md:text-lg leading-relaxed max-w-md">
              Share a few details and we&apos;ll respond within one business day with next steps and a transparent quote.
            </p>

            <div className="mt-12 space-y-6">
              <div>
                <div className="overline mb-2">Service area</div>
                <div className="font-serif-display text-2xl text-text-main">{BUSINESS.serviceArea}</div>
              </div>
              <div>
                <div className="overline mb-2">Hours</div>
                <div className="font-accent text-text-main">{BUSINESS.hours}</div>
              </div>
              <div>
                <div className="overline mb-2">Direct</div>
                <a href={`tel:${BUSINESS.phone}`} className="font-accent text-text-main hover:text-cedar" data-testid="booking-phone">
                  {BUSINESS.phone}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="booking-success"
                className="border border-[#E5E0D8] bg-surface p-10 md:p-14 rounded-2xl"
              >
                <p className="overline mb-4">Thank you</p>
                <h3 className="font-serif-display text-3xl md:text-4xl text-text-main">
                  Your request is in good hands.
                </h3>
                <p className="text-text-muted mt-6 max-w-lg leading-relaxed">
                  We&apos;ve received the details and one of our crew leads will reach out within one business day. In
                  the meantime, feel free to browse our recent work or reach us directly at{" "}
                  <a href={`mailto:${BUSINESS.email}`} className="text-cedar underline underline-offset-4">
                    {BUSINESS.email}
                  </a>
                  .
                </p>
                <button
                  data-testid="booking-reset"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({
                      full_name: "",
                      email: "",
                      phone: "",
                      address: "",
                      sauna_type: "indoor",
                      sauna_model: "",
                      service_type: "assembly",
                      notes: "",
                    });
                    setDate(null);
                  }}
                  className="mt-10 inline-flex items-center gap-2 rounded-full border border-cedar text-cedar hover:bg-cedar hover:text-white px-6 py-3 font-accent text-sm"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="booking-form"
                className="border border-[#E5E0D8] bg-surface p-8 md:p-12 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
              >
                <div>
                  <label className="overline block mb-3">Full name</label>
                  <input
                    data-testid="booking-input-name"
                    value={form.full_name}
                    onChange={update("full_name")}
                    className="editorial-input"
                    placeholder="Ingrid Sørensen"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Email</label>
                  <input
                    data-testid="booking-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="editorial-input"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Phone</label>
                  <input
                    data-testid="booking-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    className="editorial-input"
                    placeholder="(503) 555-0100"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Install address</label>
                  <input
                    data-testid="booking-input-address"
                    value={form.address}
                    onChange={update("address")}
                    className="editorial-input"
                    placeholder="City, state or full address"
                    required
                  />
                </div>

                <div>
                  <label className="overline block mb-3">Sauna type</label>
                  <Select value={form.sauna_type} onValueChange={(v) => setForm((f) => ({ ...f, sauna_type: v }))}>
                    <SelectTrigger
                      data-testid="booking-select-type"
                      className="border-0 border-b border-[#E5E0D8] rounded-none bg-transparent px-1 focus:ring-0 focus:border-cedar h-11 font-sans"
                    >
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indoor">Indoor</SelectItem>
                      <SelectItem value="outdoor">Outdoor</SelectItem>
                      <SelectItem value="unsure">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="overline block mb-3">Sauna brand / model</label>
                  <input
                    data-testid="booking-input-model"
                    value={form.sauna_model}
                    onChange={update("sauna_model")}
                    className="editorial-input"
                    placeholder="e.g. Almost Heaven Grandview"
                    required
                  />
                </div>

                <div>
                  <label className="overline block mb-3">Service needed</label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
                  >
                    <SelectTrigger
                      data-testid="booking-select-service"
                      className="border-0 border-b border-[#E5E0D8] rounded-none bg-transparent px-1 focus:ring-0 focus:border-cedar h-11 font-sans"
                    >
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="overline block mb-3">Preferred date (optional)</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="booking-date-trigger"
                        className="editorial-input text-left flex items-center justify-between"
                      >
                        <span className={date ? "text-text-main" : "text-text-muted opacity-70"}>
                          {date ? format(date, "PPP") : "Pick a date"}
                        </span>
                        <CalendarIcon size={16} strokeWidth={1.5} className="text-cedar" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-bone border-[#E5E0D8]" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="md:col-span-2">
                  <label className="overline block mb-3">Notes</label>
                  <textarea
                    data-testid="booking-input-notes"
                    value={form.notes}
                    onChange={update("notes")}
                    rows={4}
                    className="editorial-input resize-none"
                    placeholder="Delivery date, site conditions, questions…"
                  />
                </div>

                <div className="md:col-span-2 flex items-center justify-between mt-2">
                  <p className="text-xs font-accent text-text-muted max-w-xs">
                    By submitting, you agree we may contact you about your request. No spam, ever.
                  </p>
                  <button
                    data-testid="booking-submit"
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-full bg-cedar text-white hover:bg-cedar-hover disabled:opacity-60 px-8 py-4 font-accent text-sm tracking-wide transition-colors"
                  >
                    {submitting ? "Sending…" : "Send request"}
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
